import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarDays, ExternalLink, Link2, Unplug } from 'lucide-react';
import { GoogleCalendarConnectionResponse, GoogleCalendarConnectResponse } from '@diana/contracts';
import { useAdminApi } from '../../app/AdminApiContext.js';
import { Button } from '../../ui/index.js';
import './calendar.css';

export function CalendarSettings() {
  const api = useAdminApi(),
    cache = useQueryClient();
  const query = useQuery({
    queryKey: ['google-calendar'],
    queryFn: () => api.get('/api/v1/admin/calendar', GoogleCalendarConnectionResponse),
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  async function connect() {
    setBusy(true);
    setError('');
    try {
      const result = await api.request('/api/v1/admin/calendar/connect', {
        method: 'POST',
        schema: GoogleCalendarConnectResponse,
      });
      window.location.assign(result.authorizationUrl);
    } catch {
      setError('Could not start Google sign-in. Try again.');
      setBusy(false);
    }
  }
  async function retryFailed() {
    setBusy(true);
    setError('');
    try {
      const result = await api.request('/api/v1/admin/calendar/retry', {
        method: 'POST',
        schema: GoogleCalendarConnectionResponse,
      });
      cache.setQueryData(['google-calendar'], result);
    } catch {
      setError('Could not retry sync. Try again.');
    } finally {
      setBusy(false);
    }
  }
  async function disconnect() {
    if (!window.confirm('Disconnect Google Calendar? Existing calendar events will stay.')) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.request('/api/v1/admin/calendar/disconnect', {
        method: 'POST',
        schema: GoogleCalendarConnectionResponse,
      });
      cache.setQueryData(['google-calendar'], result);
      await cache.invalidateQueries({ queryKey: ['shoot-calendar'] });
    } catch {
      setError('Could not disconnect. Try again.');
    } finally {
      setBusy(false);
    }
  }
  const value = query.data?.connection;
  return (
    <section className="calendar-settings">
      <header>
        <CalendarDays size={24} aria-hidden="true" />
        <h2>Google Calendar</h2>
      </header>
      <p>Scheduled shoots, kept up to date in Diana’s calendar.</p>
      {query.isPending ? (
        <p role="status">Checking connection…</p>
      ) : query.isError ? (
        <div role="alert">
          <p>Could not load the connection.</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      ) : value ? (
        <>
          {!value.configured ? (
            <div className="calendar-settings__notice">
              <strong>Connection setup needed</strong>
              <p>
                Add the Google OAuth credentials to the local API, then connect Diana’s account
                here. The existing shoots calendar will be used.
              </p>
            </div>
          ) : (
            <>
              <dl>
                <div>
                  <dt>Account</dt>
                  <dd>{value.accountEmail || 'Not connected'}</dd>
                </div>
                <div>
                  <dt>Calendar</dt>
                  <dd>{value.calendarName || 'Existing shoots calendar'}</dd>
                </div>
              </dl>
              {value.needsReconnect ? (
                <p role="alert">Reconnect the original Google account to resume sync.</p>
              ) : null}
              {value.errorCount > 0 ? (
                <div role="alert">
                  <p>{value.errorCount} calendar updates need attention.</p>
                  <Button
                    variant="outline"
                    loading={busy}
                    disabled={value.needsReconnect || !value.connected}
                    onClick={() => void retryFailed()}
                  >
                    Retry failed updates
                  </Button>
                </div>
              ) : null}
              <div className="calendar-settings__actions">
                {!value.connected || value.needsReconnect ? (
                  <Button
                    variant="primary"
                    leadingIcon={Link2}
                    loading={busy}
                    onClick={() => void connect()}
                  >
                    {value.needsReconnect ? 'Reconnect Google' : 'Connect Google'}
                  </Button>
                ) : null}
                {value.connected && value.calendarUrl ? (
                  <a
                    className="dui-button dui-button--outline dui-button--medium"
                    href={value.calendarUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink size={16} aria-hidden="true" />
                    Open calendar
                  </a>
                ) : null}
                {value.connected ? (
                  <Button
                    variant="quiet"
                    leadingIcon={Unplug}
                    loading={busy}
                    onClick={() => void disconnect()}
                  >
                    Disconnect
                  </Button>
                ) : null}
              </div>
            </>
          )}
          <p className="calendar-settings__hint">
            Connecting does not import old shoots. Save a Scheduled shoot or use its calendar button
            to sync it. Client invitations are never sent.
          </p>
        </>
      ) : null}
      <p className="calendar-settings__hint">
        <a href="/app-info/privacy/">Google Calendar data use</a>
        {' · '}
        <a href="/app-info/terms/">Terms of use</a>
      </p>
      {error ? <p role="alert">{error}</p> : null}
    </section>
  );
}

export function CalendarCallback() {
  const api = useAdminApi(),
    navigate = useNavigate(),
    cache = useQueryClient();
  const [params] = useState(() => new URLSearchParams(window.location.search));
  const operation = useRef<Promise<unknown> | null>(null),
    [error, setError] = useState(false);
  useEffect(() => {
    window.history.replaceState(window.history.state, '', window.location.pathname);
    let active = true;
    if (!operation.current) {
      const code = params.get('code'),
        state = params.get('state');
      operation.current =
        code && state && !params.has('error')
          ? api.request('/api/v1/admin/calendar/callback', {
              method: 'POST',
              body: { code, state },
              schema: GoogleCalendarConnectionResponse,
            })
          : Promise.reject(new Error('Sign-in canceled'));
    }
    void operation.current
      .then(async () => {
        if (active) {
          await cache.invalidateQueries({ queryKey: ['google-calendar'] });
          navigate('/settings/calendar', { replace: true });
        }
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [api, params, navigate, cache]);
  return (
    <section className="calendar-settings">
      <h2>{error ? 'Could not connect' : 'Connecting calendar…'}</h2>
      {error ? (
        <>
          <p>
            Check that you chose Diana’s account with write access to the shoots calendar, then try
            again.
          </p>
          <Link to="/settings/calendar">Back to Google Calendar</Link>
        </>
      ) : (
        <p role="status">Verifying your account and calendar access.</p>
      )}
    </section>
  );
}
